import { cleanFloat } from "@/lib/tool-kit/grades/rounding";

/**
 * Words correct per minute (WCPM), accuracy, and the Hasbrouck & Tindal (2017) oral
 * reading fluency norms.
 *
 * Norms: Hasbrouck, J., & Tindal, G. (2017). *An update to compiled ORF norms*
 * (Technical Report No. 1702). Behavioral Research and Teaching, University of Oregon
 * (ERIC ED594994), Table 4. Compiled from DIBELS 6th Edition, DIBELS Next and easyCBM
 * scores; grade 1 has no fall norms. WCPM on a one-minute, unpracticed, grade-level
 * passage; errors are words misread, omitted, read out of order, or supplied by the
 * examiner after a 3-second pause.
 *
 * Accuracy bands default to the running-record bands in the Louisiana Department of
 * Education's "How to Take Running Records" handout, after Marie Clay: easy 96–100%,
 * instructional 90–95%, hard below 90%. Conventions differ, so the widget lets the
 * reader set both cutoffs.
 */

export type Season = "fall" | "winter" | "spring";
export type NormPercentile = 10 | 25 | 50 | 75 | 90;
export const NORM_PERCENTILES: readonly NormPercentile[] = [90, 75, 50, 25, 10];

export type NormRow = Record<NormPercentile, number>;

const row = (p90: number, p75: number, p50: number, p25: number, p10: number) =>
  ({ 90: p90, 75: p75, 50: p50, 25: p25, 10: p10 }) as NormRow;

/** Table 4, by grade then season. `null`: no norm published. */
export const ORF_NORMS_2017: Record<number, Record<Season, NormRow | null>> = {
  1: {
    fall: null,
    winter: row(97, 59, 29, 16, 9),
    spring: row(116, 91, 60, 34, 18),
  },
  2: {
    fall: row(111, 84, 50, 36, 23),
    winter: row(131, 109, 84, 59, 35),
    spring: row(148, 124, 100, 72, 43),
  },
  3: {
    fall: row(134, 104, 83, 59, 40),
    winter: row(161, 137, 97, 79, 62),
    spring: row(166, 139, 112, 91, 63),
  },
  4: {
    fall: row(153, 125, 94, 75, 60),
    winter: row(168, 143, 120, 95, 71),
    spring: row(184, 160, 133, 105, 83),
  },
  5: {
    fall: row(179, 153, 121, 87, 64),
    winter: row(183, 160, 133, 109, 84),
    spring: row(195, 169, 146, 119, 102),
  },
  6: {
    fall: row(185, 159, 132, 112, 89),
    winter: row(195, 166, 145, 116, 91),
    spring: row(204, 173, 146, 122, 91),
  },
};

export interface FluencyResult {
  correct: number;
  wcpm: number;
  accuracy: number;
}

export function fluency(
  wordsRead: number,
  errors: number,
  seconds: number
): FluencyResult | null {
  if (wordsRead <= 0 || errors < 0 || errors > wordsRead || seconds <= 0) {
    return null;
  }
  const correct = wordsRead - errors;
  return {
    correct,
    wcpm: cleanFloat((correct * 60) / seconds),
    accuracy: cleanFloat((correct * 100) / wordsRead),
  };
}

/**
 * Where a score sits among the published percentiles: the highest percentile it reaches,
 * or null when it is below the 10th.
 */
export function percentileReached(
  wcpm: number,
  norms: NormRow
): NormPercentile | null {
  for (const percentile of NORM_PERCENTILES) {
    if (wcpm >= norms[percentile]) {
      return percentile;
    }
  }
  return null;
}

export type AccuracyBand = "independent" | "instructional" | "frustration";

export function accuracyBand(
  accuracy: number,
  independentMin: number,
  instructionalMin: number
): AccuracyBand {
  const value = cleanFloat(accuracy);
  if (value >= independentMin) {
    return "independent";
  }
  if (value >= instructionalMin) {
    return "instructional";
  }
  return "frustration";
}
